import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import axios from 'lib/axios'
import { buildInsightsSearchBody, getInsightsBase } from 'lib/insightsApi'
import { normalizeMetricStatus } from 'lib/metricStatus'
import dayjs from 'dayjs'
import { InsightSubType, SystemEventName } from 'models'
import { CategoriesWeightsResponse } from 'models/Framework'
import { PerformanceScoreCategory, PerformanceScoreCpi, PerformanceStatus } from 'models/PerformanceScoreChart'
import { Benchmark, BenchmarkScoreCategory, BenchmarkScoreCategoryData } from 'models/Benchmark'
import { CPILibraryItem, DataStatus } from 'models/CPILibrary'
import { MetricsServiceV4 } from 'services/v4/metrics-v4.service'
import { BenchmarkServiceV4 } from 'services/v4/benchmark-v4.service'
import { dateFormat, getHealthDirection, isCustomFramework } from 'lib/utils'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { accountSettingsSelector } from 'store/user/userSlice'
import { performanceLastUpdateSelector, setPerformanceLastUpdate } from 'store/dashboard/dashboardSlice'
import { categoryToUrl, formatPerformanceUnitDisplay, getSystemEventValue } from '../library/utils'
import { HistoryPerformanceData, HistoryBenchmarkData } from 'models/CategoryPage'
import { formatChartXAxisLabels, generatePeriodDates } from './categoryDashboard.utils'
import {
  ActiveCPI,
  AvailableCPI,
  BenchmarkChartPoint,
  BenchmarkingData,
  CategoryDashboardData,
  CategoryMetaData,
  CPIPeriod,
  InsightsSummary,
  LabelItem,
  PerformanceData,
  TrendDataPoint
} from './categoryDashboard.types'

interface Category {
  id: string
  title: string
}

interface UseCategoryDashboardDataReturn {
  data: CategoryDashboardData | null
  allCategories: Category[]
  categoryName: string
  isLoading: boolean
  isPerformanceLoading: boolean
  isBenchmarkingLoading: boolean
  isRecentFindingsLoading: boolean
  isPerformanceHistoryLoading: boolean
  isBenchmarkHistoryLoading: boolean
  isInsightsLoading: boolean
  isLabelsLoading: boolean
  isAvailableCPIsLoading: boolean
  onPerformanceTimePeriodChange: (period: CPIPeriod) => void
  onBenchmarkTimePeriodChange: (period: CPIPeriod) => void
  industryOptions: string[]
  onIndustryChange: (industry: string) => void
  performanceCpis: PerformanceScoreCpi[]
  benchmarkCpis: BenchmarkScoreCategoryData[]
  refetchActiveAndAvailable: () => Promise<void>
}

const useCategoryDashboardData = (urlCategoryId: string | undefined): UseCategoryDashboardDataReturn => {
  const dispatch = useAppDispatch()
  const accountSettings = useAppSelector(accountSettingsSelector)
  const framework = accountSettings?.cyFramework
  const [allCategories, setAllCategories] = useState<Category[]>([])
  const [activeCategoryIds, setActiveCategoryIds] = useState<Set<string>>(new Set())
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined)
  const [categoryName, setCategoryName] = useState('')

  const [metaData, setMetaData] = useState<CategoryMetaData | null>(null)
  const [categoryWeight, setCategoryWeight] = useState<string | null>(null)
  const globalLastUpdate = useAppSelector(performanceLastUpdateSelector)

  // Fetch the global performance_score once to (a) populate the last-update
  // timestamp if the store has none and (b) determine which categories have
  // active CPIs so the navigation dropdown can hide categories that don't.
  useEffect(() => {
    const fetchPerformanceOverview = async () => {
      try {
        const res = await axios.get('/api/v3/cpis/performance_score')
        if (res.status === 200) {
          // Always take the date this fetch answered with. Writing it only into an empty store
          // pinned the page to the first date seen in the session, so a score that recalculated
          // while the tab was open kept reporting the update that preceded it.
          const date = res.data.data?.date
          if (date) {
            dispatch(setPerformanceLastUpdate(dayjs(date).format(dateFormat.lastUpdate)))
          }
          const categories: PerformanceScoreCategory[] = res.data.data?.categories || []
          const activeIds = new Set(
            categories
              .filter((c) => c.status === PerformanceStatus.active && Array.isArray(c.details) && c.details.length > 0)
              .map((c) => c.id)
          )
          setActiveCategoryIds(activeIds)
        }
      } catch (err) {
        console.error('Error fetching performance overview:', err)
      }
    }
    fetchPerformanceOverview()
  }, [dispatch])

  const [performance, setPerformance] = useState<PerformanceData | null>(null)
  const [rawPerformanceHistory, setRawPerformanceHistory] = useState<HistoryPerformanceData[]>([])
  const [rawBenchmarkHistory, setRawBenchmarkHistory] = useState<HistoryBenchmarkData[]>([])
  const [performanceTimePeriod, setPerformanceTimePeriod] = useState<string>('1m')
  const [benchmarkTimePeriod, setBenchmarkTimePeriod] = useState<string>('1m')

  const [activeCPIs, setActiveCPIs] = useState<ActiveCPI[]>([])
  // Recent finding values come from the v4 metrics endpoint (recentPoint.yValue
  // and lastMonthChange), not the v3 performance score, so they resolve
  // separately and are keyed by the CPI's display id (ActiveCPI.cpiId).
  // `direction` is the metric's health direction (asc = higher-is-better,
  // desc = lower-is-better) so the card can color the change as an
  // improvement/worsening rather than by raw sign.
  const [recentFindings, setRecentFindings] = useState<
    Map<string, { value: string | null; change: number | null; direction: 'asc' | 'desc' }>
  >(new Map())
  const [performanceCpis, setPerformanceCpis] = useState<PerformanceScoreCpi[]>([])
  const [benchmarkCpis, setBenchmarkCpis] = useState<BenchmarkScoreCategoryData[]>([])
  const [availableCPIs, setAvailableCPIs] = useState<AvailableCPI[]>([])
  const [benchmarking, setBenchmarking] = useState<BenchmarkingData | null>(null)
  const [benchmarkRaw, setBenchmarkRaw] = useState<Benchmark | null>(null)
  const [industryOptions, setIndustryOptions] = useState<string[]>([])
  const [insights, setInsights] = useState<InsightsSummary | null>(null)
  const [labels, setLabels] = useState<LabelItem[]>([])

  const [isPerformanceLoading, setIsPerformanceLoading] = useState(true)
  const [isBenchmarkingLoading, setIsBenchmarkingLoading] = useState(true)
  const [isRecentFindingsLoading, setIsRecentFindingsLoading] = useState(true)
  const [isPerformanceHistoryLoading, setIsPerformanceHistoryLoading] = useState(true)
  const [isBenchmarkHistoryLoading, setIsBenchmarkHistoryLoading] = useState(true)
  const [isInsightsLoading, setIsInsightsLoading] = useState(true)
  const [isLabelsLoading, setIsLabelsLoading] = useState(true)
  const [isAvailableCPIsLoading, setIsAvailableCPIsLoading] = useState(true)

  const isLoading = isPerformanceLoading

  // Fetch all categories, resolve URL slug to API category ID
  useEffect(() => {
    if (!urlCategoryId) return
    const resolve = async () => {
      try {
        const res = await axios.get('/api/v3/frameworks/categories')
        if (res.status === 200) {
          const categories: Category[] = res.data.data.categories || []
          setAllCategories(categories)
          // Match by id directly, or by matching the URL slug to the slugified title
          const current =
            categories.find((c) => c.id === urlCategoryId) ||
            categories.find((c) => categoryToUrl(c.title) === urlCategoryId)
          setCategoryId(current?.id || urlCategoryId)
          setCategoryName(current?.title || '')
        }
      } catch (err) {
        console.error('Error fetching categories:', err)
        setCategoryId(urlCategoryId)
      }
    }
    resolve()
  }, [urlCategoryId])

  // Fetch the category weight from the framework weights endpoint
  const fetchCategoryWeight = useCallback(async () => {
    if (!categoryId) return
    try {
      let frameworkName = accountSettings?.cyFramework || 'onyxia'
      if (isCustomFramework(frameworkName)) {
        // For custom frameworks, resolve the framework ID
        const customRes = await axios.get('/api/v3/frameworks/custom')
        if (customRes.status.toString().includes('20') && customRes.data.framework) {
          frameworkName = customRes.data.framework.id
        }
      }

      const res = await axios.get<{ data: CategoriesWeightsResponse }>(
        `/api/v3/frameworks/categories/weights?fetch-category-weights&framework=${frameworkName}&all=true`
      )
      if (res.status.toString().includes('20')) {
        const categories = res.data.data.categories || []
        const current = categories.find((c) => c.id === categoryId)
        setCategoryWeight(current ? `${current.weight}%` : '-')
      }
    } catch (err) {
      console.error('Error fetching category weight:', err)
      setCategoryWeight('-')
    }
  }, [categoryId, accountSettings?.cyFramework])

  // Main API: fetch performance score for the category. `silent` skips the
  // page-wide loading flag so background polling doesn't flash the dashboard
  // skeletons every refetch.
  const fetchPerformanceScore = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!categoryId) return
    if (!silent) setIsPerformanceLoading(true)
    try {
      const res = await axios.get(`/api/v3/cpis/performance_score/${categoryId}?loadDataSources=true&loadStatus=true`)
      if (res.status === 200) {
        const categoryData: PerformanceScoreCategory = res.data.data
        const cpis: PerformanceScoreCpi[] = (categoryData.details || []).map((cpi) => ({
          ...cpi,
          status: normalizeMetricStatus(cpi.status)
        }))
        setPerformanceCpis(cpis)

        // Performance data
        setPerformance({
          score: categoryData.score ?? 0,
          changeFromLastMonth: categoryData.lastMonthChange ?? null,
          lastUpdate: '',
          trendData: []
        })

        // Active CPIs
        const active: ActiveCPI[] = cpis.map((cpi) => ({
          cpiId: cpi.cpiDisplayName || cpi.cpiName,
          cpiName: cpi.cpiName,
          name: cpi.cpiTitle || cpi.cpiDisplayName,
          performance: cpi.score ?? 0,
          changeFromLastMonth: cpi.lastMonthChange ?? null,
          weight: cpi.weight != null ? `${cpi.weight}%` : '0%',
          // Seeded as null so the card can distinguish "benchmarking not yet
          // loaded / not available" from a real 0th-percentile score. The
          // benchmarking endpoint resolves separately and may not return a
          // score for every CPI.
          benchmarkingScore: null,
          recentFindingValue:
            cpi.value != null
              ? `${Math.round(cpi.value)}${formatPerformanceUnitDisplay(cpi.unit, cpi.tooltipUnit)}`
              : '',
          // Sourced from the v4 metrics endpoint (lastMonthChange) once it
          // resolves; seeded null so the card shows nothing until then.
          recentFindingChange: null,
          recentFindingDirection: 'desc',
          labelsCount: 0,
          status: cpi.status
        }))
        setActiveCPIs(active)

        // Meta data
        const activeCpiCount = cpis.length
        const uniqueIntegrations = new Set(cpis.flatMap((cpi) => (cpi.dataSources || []).map((ds) => ds.name)))

        setMetaData({
          weight: '',
          dateActivated: '',
          activeIntegrations: uniqueIntegrations.size,
          activeCPIs: activeCpiCount
        })

        // After getting active CPIs, fetch supplementary data. SLA-changed
        // events only carry `cpi_id` (a UUID) and `cpi_title`, not
        // `cpi_display_name`, so the map is keyed by both the technical name
        // and the title to give the label resolver a reliable fallback.
        const cpiNames = cpis.map((c) => c.cpiName)
        const cpiDisplayNameMap: Record<string, string> = {}
        cpis.forEach((c) => {
          if (c.cpiName) cpiDisplayNameMap[c.cpiName] = c.cpiDisplayName
          if (c.cpiTitle) cpiDisplayNameMap[c.cpiTitle] = c.cpiDisplayName
        })
        if (cpiNames.length > 0) {
          fetchLabels(cpiNames, cpiDisplayNameMap, { silent })
          fetchInsights(cpiNames, { silent })
          fetchRecentFindings(cpis, { silent })
        } else {
          setLabels([])
          setInsights({ performanceDegradation: 0, suggestedOptimization: 0, performanceImprovement: 0 })
          setRecentFindings(new Map())
          setIsLabelsLoading(false)
          setIsInsightsLoading(false)
          setIsRecentFindingsLoading(false)
        }
      }
    } catch (err) {
      console.error('Error fetching performance score:', err)
    } finally {
      setIsPerformanceLoading(false)
    }
  }, [categoryId])

  // Recent finding values are sourced from the v4 metrics endpoint
  // (recentPoint.yValue) rather than the v3 performance score. The /latest
  // payload mirrors the CPI trends page default by reusing data already loaded
  // to render the cards: the CPI's `section` and its configured integration
  // instance names, both from the performance_score response. The resulting
  // map is keyed by the CPI's display id (matching ActiveCPI.cpiId) and merged
  // onto the cards once it resolves.
  const fetchRecentFindings = useCallback(
    async (cpis: PerformanceScoreCpi[], { silent = false }: { silent?: boolean } = {}) => {
      if (!silent) setIsRecentFindingsLoading(true)
      try {
        const entries = await Promise.all(
          cpis.map(async (cpi) => {
            const key = cpi.cpiDisplayName || cpi.cpiName
            const slug = cpi.cpiName
            if (!slug) return null
            try {
              const { data } = await MetricsServiceV4.getLatest(slug, {
                section: cpi.section ? cpi.section.toLowerCase() : undefined,
                instance_names: cpi.instanceNames || []
              })
              const yValue = data?.recentPoint?.yValue
              const change = data?.lastMonthChange ?? null
              const value =
                yValue != null
                  ? `${Math.round(yValue)}${formatPerformanceUnitDisplay(cpi.unit, cpi.tooltipUnit)}`
                  : null
              if (value == null && change == null) return null
              // Health direction (lower- vs higher-is-better) from the metric's
              // zone, recent value and SLA — defaults to 'desc' when unknown.
              const direction =
                data?.zoneColor && yValue != null && data?.sla != null
                  ? getHealthDirection(data.zoneColor, yValue, data.sla)
                  : 'desc'
              return [key, { value, change, direction }] as const
            } catch (err) {
              console.error(`Error fetching latest metric for ${slug}:`, err)
              return null
            }
          })
        )
        setRecentFindings(
          new Map(
            entries.filter(Boolean) as [
              string,
              { value: string | null; change: number | null; direction: 'asc' | 'desc' }
            ][]
          )
        )
      } finally {
        setIsRecentFindingsLoading(false)
      }
    },
    []
  )

  // Extract benchmarking data for a given industry
  const extractBenchmarkForIndustry = useCallback(
    (benchmark: Benchmark, industryName: string) => {
      const industryData = benchmark.score.find((s) => s.industry_name === industryName) || benchmark.score[0]
      if (!industryData) return

      const category: BenchmarkScoreCategory | undefined = industryData.categories?.find(
        (c: BenchmarkScoreCategory) => c.category_id === categoryId
      )

      if (category?.summary) {
        setBenchmarking({
          industry: industryData.industry_name || 'All Industries',
          lessStrict: category.summary.less ?? 0,
          sameSLA: category.summary.equal ?? 0,
          moreStrict: category.summary.more ?? 0,
          lastUpdate: '',
          chartData: []
        })
        setBenchmarkCpis(category.data || [])
      } else {
        setBenchmarking(null)
        setBenchmarkCpis([])
      }
    },
    [categoryId]
  )

  // Fetch benchmarking data
  const fetchBenchmarking = useCallback(async () => {
    if (!categoryId || !accountSettings) return
    // Account settings are in, but this tenant has no framework to benchmark against.
    if (!framework) {
      setIsBenchmarkingLoading(false)
      return
    }
    setIsBenchmarkingLoading(true)
    try {
      const benchmark = await BenchmarkServiceV4.getCpiIndustryScores(framework)
      if (benchmark) {
        setBenchmarkRaw(benchmark)
        const names = benchmark.score.map((s) => s.industry_name)
        const sortedNames = [
          ...names.filter((n) => n === 'All Industries'),
          ...names.filter((n) => n !== 'All Industries')
        ]
        setIndustryOptions(sortedNames)
        extractBenchmarkForIndustry(benchmark, 'All Industries')
      }
    } catch (err) {
      console.error('Error fetching benchmarking:', err)
    } finally {
      setIsBenchmarkingLoading(false)
    }
  }, [categoryId, accountSettings, framework, extractBenchmarkForIndustry])

  // Handle industry change from UI
  const onIndustryChange = useCallback(
    (industry: string) => {
      if (!benchmarkRaw) return
      extractBenchmarkForIndustry(benchmarkRaw, industry)
    },
    [benchmarkRaw, extractBenchmarkForIndustry]
  )

  // Fetch available (inactive) CPIs for this category
  const fetchAvailableCPIs = useCallback(async () => {
    if (!categoryId) return
    setIsAvailableCPIsLoading(true)
    try {
      // First get active data sources
      const configuredRes = await axios.get('/api/v3/integrations/configured')
      const instances: { id: string; integration_name: string; instance_name?: string }[] =
        configuredRes.data.instances || []
      const configuredNames = Array.from(new Set(instances.map((i) => i.integration_name)))
      /*
       * The search reads a missing `ds` as "any source", so asking without one answers with every
       * inactive CPI in the category. With no configured source nothing is activatable, so there is
       * nothing to offer and nothing to ask for.
       * */
      const hasConfiguredSources = configuredNames.length > 0

      // Fetch inactive CPIs that match active data sources
      const params = new URLSearchParams({
        category: categoryId,
        status: 'inactive',
        sortBy: 'desc'
      })
      if (hasConfiguredSources) {
        params.set('ds', configuredNames.join(','))
      }

      // Fetch inactive CPIs and all CPIs (for dateActivated) in parallel
      const [inactiveRes, allCpisRes] = await Promise.all([
        hasConfiguredSources ? axios.get(`/api/v3/cpis/search?${params.toString()}`) : Promise.resolve(null),
        axios.get(`/api/v3/cpis/search?category=${categoryId}`)
      ])

      if (!hasConfiguredSources) {
        setAvailableCPIs([])
      } else if (inactiveRes && inactiveRes.status === 200) {
        const cpis: CPILibraryItem[] = inactiveRes.data.data.cpis || []
        const available: AvailableCPI[] = cpis.map((cpi) => {
          const matchingIntegrations = cpi.supported_integrations?.filter((i) => configuredNames.includes(i.name)) || []
          const matchingInstances = instances.filter((inst) =>
            matchingIntegrations.some((si) => si.name === inst.integration_name)
          )

          return {
            cpiId: cpi.display_name || cpi.name,
            cpiName: cpi.name,
            name: cpi.title || cpi.display_name,
            description: cpi.short_description || cpi.long_description || '',
            dataSourceLogos: matchingIntegrations.map((i) => i.logo_url),
            isV2: cpi.is_v2,
            configuredIntegrationIds: matchingInstances.map((inst) => inst.id),
            configuredInstanceNames: matchingInstances
              .filter((inst) => inst.instance_name)
              .map((inst) => inst.instance_name as string)
          }
        })
        setAvailableCPIs(available)
      }

      // Date activated: earliest CPI creation date from configured CPIs
      if (allCpisRes.status === 200) {
        const allCpis: CPILibraryItem[] = allCpisRes.data.data.cpis || []
        const dates = allCpis
          .filter((cpi) => cpi.configuration?.created_at)
          .map((cpi) => dayjs(cpi.configuration?.created_at))
          .filter((d) => d.isValid())

        const earliest = dates.length > 0 ? dates.reduce((min, d) => (d.isBefore(min) ? d : min)) : null
        setMetaData((prev) =>
          prev ? { ...prev, dateActivated: earliest ? earliest.format('MM/DD/YYYY') : '-' } : prev
        )
      }
    } catch (err) {
      console.error('Error fetching available CPIs:', err)
    } finally {
      setIsAvailableCPIsLoading(false)
    }
  }, [categoryId])

  // Fetch insights summary for category CPIs
  const fetchInsights = useCallback(async (cpiNames: string[], { silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setIsInsightsLoading(true)
    try {
      const base = await getInsightsBase()
      const today = dayjs().format('YYYY-MM-DD')
      const thirtyDaysAgo = dayjs().subtract(30, 'days').format('YYYY-MM-DD')

      const res = await axios.post(
        `${base}/search`,
        buildInsightsSearchBody(
          {
            sortBy: 'created_at',
            sortDirection: 'DESC',
            dateFrom: thirtyDaysAgo,
            dateTo: today,
            cpis: cpiNames
          },
          base
        )
      )

      if (res.status.toString().includes('20')) {
        const data = res.data.data || []
        let performanceDegradation = 0
        let suggestedOptimization = 0
        let performanceImprovement = 0

        data.forEach((item: any) => {
          // v2 items carry `type` instead of `sub_type` (identical enum values).
          const subType = item.sub_type ?? item.type
          if (subType === InsightSubType.CrossedIntoGreen || subType === InsightSubType.Improvement) {
            performanceImprovement += 1
          }
          if (subType === InsightSubType.Reduction || subType === InsightSubType.CrossedIntoRed) {
            performanceDegradation += 1
          }
          if (subType === InsightSubType.CpiSlaOptimization) {
            suggestedOptimization += 1
          }
        })

        setInsights({ performanceDegradation, suggestedOptimization, performanceImprovement })
      }
    } catch (err) {
      console.error('Error fetching insights:', err)
    } finally {
      setIsInsightsLoading(false)
    }
  }, [])

  // Fetch labels for category CPIs (bulk endpoint)
  const fetchLabels = useCallback(
    async (
      cpiNames: string[],
      displayNameMap: Record<string, string>,
      { silent = false }: { silent?: boolean } = {}
    ) => {
      if (!silent) setIsLabelsLoading(true)
    try {
      const res = await axios.post('/api/v3/labels/cpis', {
        frequency: 'daily',
        time_period: '1y',
        cpis: cpiNames
      })

      if (res.status.toString().includes('20')) {
        const labelsData = res.data.data
        const allLabels: LabelItem[] = []

        if (labelsData) {
          const rawLabels: any[] = []
          Object.values(labelsData).forEach((labelsForDate: any) => {
            if (!Array.isArray(labelsForDate)) return
            labelsForDate.forEach((label: any) => rawLabels.push(label))
          })

          // Sort by date descending (latest first)
          rawLabels.sort((a, b) => {
            const dateA = dayjs(a.data?.date || a.created_at)
            const dateB = dayjs(b.data?.date || b.created_at)
            return dateB.valueOf() - dateA.valueOf()
          })

          rawLabels.forEach((label) => {
            allLabels.push({
              id: label.id || `${label.action}-${label.created_at}`,
              type: label.action || '',
              title: label.data?.title || getSystemEventValue(label.action) || '',
              cpiName:
                label.data?.cpi_display_name ||
                displayNameMap[label.data?.cpi_id] ||
                displayNameMap[label.data?.cpi_title] ||
                '',
              description: label.data?.description || label.description || '',
              date:
                label.data?.date || label.created_at
                  ? dayjs(label.data?.date || label.created_at).format('MMM DD, YYYY')
                  : '',
              isPublic: label.data?.is_public ?? true,
              author: label.action === SystemEventName.CpiCustom ? label.username || undefined : undefined,
              slaChange: undefined
            })
          })
        }

        setLabels(allLabels)
      }
    } catch (err) {
      console.error('Error fetching labels:', err)
    } finally {
      setIsLabelsLoading(false)
    }
  }, [])

  // Performance and benchmark histories are fetched independently so each
  // chart's time-period selector can act on its own chart without refetching
  // or re-rendering the other.
  const fetchPerformanceHistory = useCallback(
    async (timePeriod: string) => {
      if (!categoryId) return
      setIsPerformanceHistoryLoading(true)
      try {
        const res = await axios.post('/api/v3/cpis/performance_history', { timePeriod, category: categoryId })
        const perfData: HistoryPerformanceData[] = res?.status === 201 ? res.data.data || [] : []
        setRawPerformanceHistory(perfData)
        setPerformanceTimePeriod(timePeriod)
      } catch (err) {
        console.error('Error fetching performance history:', err)
        setRawPerformanceHistory([])
        setPerformanceTimePeriod(timePeriod)
      } finally {
        setIsPerformanceHistoryLoading(false)
      }
    },
    [categoryId]
  )

  const fetchBenchmarkHistory = useCallback(
    async (timePeriod: string) => {
      if (!categoryId) return
      setIsBenchmarkHistoryLoading(true)
      try {
        const benchList = await BenchmarkServiceV4.getHistory({ timePeriod, category: categoryId })
        setRawBenchmarkHistory(benchList)
        setBenchmarkTimePeriod(timePeriod)
      } catch (err) {
        console.error('Error fetching benchmark history:', err)
        setRawBenchmarkHistory([])
        setBenchmarkTimePeriod(timePeriod)
      } finally {
        setIsBenchmarkHistoryLoading(false)
      }
    },
    [categoryId]
  )

  // The benchmark history endpoint returns one entry per industry; pick the
  // values for the currently selected industry (falls back to the first entry).
  const selectedIndustry = benchmarking?.industry

  // Expand the API's sparse response onto a continuous weekly timeline that
  // spans the full *selected* period (not just the API's data window). Missing
  // weeks become null/0 placeholders so the x-axis shows every week the period
  // should contain — matching the "empty strip" behavior the Benchmarking
  // chart was designed around and keeping the Performance and Benchmarking
  // x-axes aligned even when one chart has data outside the other's range.
  const performanceTrendData: TrendDataPoint[] = useMemo(() => {
    if (rawPerformanceHistory.length === 0) return []
    const apiDates = rawPerformanceHistory.map((d) => dayjs(d.xValue).format('YYYY-MM-DD'))
    const allDates = generatePeriodDates(apiDates, performanceTimePeriod)
    const xLabels = formatChartXAxisLabels(allDates, performanceTimePeriod)
    const perfByDate = new Map(rawPerformanceHistory.map((d) => [dayjs(d.xValue).format('YYYY-MM-DD'), d.yValue]))
    return allDates.map((date, i) => ({
      date: dayjs(date).format('MMM DD'),
      fullDate: date,
      xLabel: xLabels[i] ?? '',
      value: perfByDate.get(date) ?? null,
      tooltipAnchor: 0 as const
    }))
  }, [rawPerformanceHistory, performanceTimePeriod])

  const benchmarkChartData: BenchmarkChartPoint[] = useMemo(() => {
    const benchEntry = rawBenchmarkHistory.find((b) => b.industry_name === selectedIndustry) || rawBenchmarkHistory[0]
    const benchValues = benchEntry?.values || []
    if (benchValues.length === 0) return []
    const apiDates = benchValues.map((v) => dayjs(v.date).format('YYYY-MM-DD'))
    const allDates = generatePeriodDates(apiDates, benchmarkTimePeriod)
    const xLabels = formatChartXAxisLabels(allDates, benchmarkTimePeriod)
    const benchByDate = new Map(benchValues.map((v) => [dayjs(v.date).format('YYYY-MM-DD'), v]))
    return allDates.map((date, i) => {
      const b = benchByDate.get(date)
      return {
        date: dayjs(date).format('MMM DD'),
        fullDate: date,
        xLabel: xLabels[i] ?? '',
        less: b?.less ?? 0,
        same: b?.equal ?? 0,
        more: b?.more ?? 0
      }
    })
  }, [rawBenchmarkHistory, benchmarkTimePeriod, selectedIndustry])

  const onPerformanceTimePeriodChange = useCallback(
    (period: CPIPeriod) => {
      fetchPerformanceHistory(period.toLowerCase())
    },
    [fetchPerformanceHistory]
  )

  const onBenchmarkTimePeriodChange = useCallback(
    (period: CPIPeriod) => {
      fetchBenchmarkHistory(period.toLowerCase())
    },
    [fetchBenchmarkHistory]
  )

  // Derive per-CPI label counts and benchmarking scores. `activeCPIs` is
  // seeded with zeros because labels and benchmarking data come from separate
  // endpoints that resolve after the performance call. The benchmarking score
  // shown on each card is the API-provided percentile in `summary.score`.
  const activeCPIsWithDerivedMetrics = useMemo(() => {
    const labelCounts = new Map<string, number>()
    labels.forEach((label) => {
      if (!label.cpiName) return
      labelCounts.set(label.cpiName, (labelCounts.get(label.cpiName) || 0) + 1)
    })
    // Only record a benchmark score when the API actually returned one for
    // that CPI. Falling back to 0 here would render "0th percentile" for
    // CPIs that have no benchmarking data — indistinguishable from a CPI
    // that genuinely scored at the 0th percentile.
    const benchmarkScores = new Map<string, number>()
    benchmarkCpis.forEach((b) => {
      if (!b.cpi_display_name) return
      if (b.summary?.score == null) return
      benchmarkScores.set(b.cpi_display_name, b.summary.score)
    })
    return activeCPIs.map((cpi) => {
      const score = benchmarkScores.get(cpi.cpiId)
      const recent = recentFindings.get(cpi.cpiId)
      return {
        ...cpi,
        labelsCount: labelCounts.get(cpi.cpiId) ?? cpi.labelsCount,
        benchmarkingScore: score !== undefined ? score : cpi.benchmarkingScore,
        recentFindingValue: recent?.value ?? cpi.recentFindingValue,
        recentFindingChange: recent?.change ?? cpi.recentFindingChange,
        recentFindingDirection: recent?.direction ?? cpi.recentFindingDirection
      }
    })
  }, [activeCPIs, labels, benchmarkCpis, recentFindings])

  // Fetch all data once categoryId is resolved
  useEffect(() => {
    if (!categoryId) return
    // Clear the prior category's data so the UI doesn't flash stale content
    // (benchmarking summary, active/available CPIs, history points) while the
    // new fetches are in flight or if the new category returns no data.
    setBenchmarking(null)
    setBenchmarkRaw(null)
    setBenchmarkCpis([])
    setRawBenchmarkHistory([])
    setRawPerformanceHistory([])
    setActiveCPIs([])
    setRecentFindings(new Map())
    setAvailableCPIs([])
    setIsAvailableCPIsLoading(true)
    setIsRecentFindingsLoading(true)
    fetchPerformanceScore()
    fetchCategoryWeight()
    fetchAvailableCPIs()
    fetchPerformanceHistory('1m')
    fetchBenchmarkHistory('1m')
  }, [
    categoryId,
    fetchPerformanceScore,
    fetchCategoryWeight,
    fetchAvailableCPIs,
    fetchPerformanceHistory,
    fetchBenchmarkHistory
  ])

  // Kept apart from the other category fetches: industry scores are scoped to the tenant's
  // framework, which may only land after the page has mounted.
  useEffect(() => {
    fetchBenchmarking()
  }, [fetchBenchmarking])

  const refetchActiveAndAvailable = useCallback(async () => {
    await Promise.all([fetchPerformanceScore(), fetchAvailableCPIs()])
  }, [fetchPerformanceScore, fetchAvailableCPIs])

  // Poll the performance score while any active CPI is still computing
  // (Initializing/Recalculating). React Query handles the interval, dedupe and
  // cleanup; the query is disabled once every CPI settles into Ready so we
  // stop hitting the API in steady state.
  const hasPendingCpis = activeCPIs.some(
    (c) => c.status === DataStatus.Initializing || c.status === DataStatus.Recalculating
  )
  useQuery(
    ['categoryDashboard.performanceScorePolling', categoryId],
    () => fetchPerformanceScore({ silent: true }),
    {
      enabled: !!categoryId && hasPendingCpis,
      refetchInterval: 15000,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false
    }
  )

  const data: CategoryDashboardData | null =
    metaData && performance
      ? {
          metaData: categoryWeight != null ? { ...metaData, weight: categoryWeight } : metaData,
          performance: { ...performance, lastUpdate: globalLastUpdate, trendData: performanceTrendData },
          benchmarking: benchmarking
            ? { ...benchmarking, lastUpdate: globalLastUpdate, chartData: benchmarkChartData }
            : {
                industry: '',
                lessStrict: 0,
                sameSLA: 0,
                moreStrict: 0,
                lastUpdate: globalLastUpdate,
                chartData: benchmarkChartData
              },
          insights: insights || {
            performanceDegradation: 0,
            suggestedOptimization: 0,
            performanceImprovement: 0
          },
          labels,
          activeCPIs: activeCPIsWithDerivedMetrics,
          availableCPIs
        }
      : null

  // Hide categories that don't have any active CPIs from the navigation
  // dropdown. Always include the current category so the user can still see
  // which one they're viewing if it happens to fall outside the active set.
  const navigableCategories = useMemo(
    () =>
      activeCategoryIds.size > 0
        ? allCategories.filter((c) => activeCategoryIds.has(c.id) || c.id === categoryId)
        : allCategories,
    [allCategories, activeCategoryIds, categoryId]
  )

  return {
    data,
    allCategories: navigableCategories,
    categoryName,
    isLoading,
    isPerformanceLoading,
    isBenchmarkingLoading,
    isRecentFindingsLoading,
    isPerformanceHistoryLoading,
    isBenchmarkHistoryLoading,
    isInsightsLoading,
    isLabelsLoading,
    isAvailableCPIsLoading,
    onPerformanceTimePeriodChange,
    onBenchmarkTimePeriodChange,
    industryOptions,
    onIndustryChange,
    performanceCpis,
    benchmarkCpis,
    refetchActiveAndAvailable
  }
}

export default useCategoryDashboardData
